import axios from 'axios';

const API_BASE = '/api';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth interceptor
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle responses
apiClient.interceptors.response.use(
  (response) => response.data,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ============== AUTH ==============
export const authService = {
  login: (email, password) =>
    apiClient.post('/auth/login', { email, password }),
  
  register: (email, password, username, displayName) =>
    apiClient.post('/auth/register', { email, password, username, displayName }),
  
  demo: () =>
    apiClient.post('/auth/demo'),
  
  getMe: () =>
    apiClient.get('/auth/me'),
  
  logout: () => {
    localStorage.removeItem('auth_token');
  },
};

// ============== SERVERS ==============
export const serverService = {
  list: () =>
    apiClient.get('/servers'),
  
  get: (serverId) =>
    apiClient.get(`/servers/${serverId}`),
  
  create: (data) =>
    apiClient.post('/servers', data),
  
  update: (serverId, data) =>
    apiClient.put(`/servers/${serverId}`, data),
  
  delete: (serverId) =>
    apiClient.delete(`/servers/${serverId}`),
  
  join: (serverId) =>
    apiClient.post(`/servers/${serverId}/join`),
  
  leave: (serverId) =>
    apiClient.post(`/servers/${serverId}/leave`),
  
  members: (serverId) =>
    apiClient.get(`/servers/${serverId}/members`),
  
  invite: (serverId, inviteCode) =>
    apiClient.post(`/servers/${serverId}/invite`, { inviteCode }),
};

// ============== CHANNELS ==============
export const channelService = {
  list: (serverId) =>
    apiClient.get(`/channels?serverId=${serverId}`),
  
  get: (channelId) =>
    apiClient.get(`/channels/${channelId}`),
  
  create: (serverId, data) =>
    apiClient.post('/channels', { ...data, serverId }),
  
  update: (channelId, data) =>
    apiClient.put(`/channels/${channelId}`, data),
  
  delete: (channelId) =>
    apiClient.delete(`/channels/${channelId}`),
};

// ============== MESSAGES ==============
export const messageService = {
  list: (channelId, limit = 50, offset = 0) =>
    apiClient.get(`/chat/channels/${channelId}/messages?limit=${limit}&offset=${offset}`),

  get: (messageId) =>
    apiClient.get(`/chat/messages/${messageId}`),

  create: (channelId, content) =>
    apiClient.post(`/chat/channels/${channelId}/messages`, { content }),

  update: (messageId, content) =>
    apiClient.patch(`/chat/messages/${messageId}`, { content }),

  delete: (messageId) =>
    apiClient.delete(`/chat/messages/${messageId}`),

  pin: (messageId) =>
    apiClient.post(`/chat/messages/${messageId}/pin`),

  unpin: (messageId) =>
    apiClient.post(`/chat/messages/${messageId}/unpin`),

  react: (messageId, emoji) =>
    apiClient.post(`/chat/messages/${messageId}/reactions`, { emoji }),

  unreact: (messageId, emoji) =>
    apiClient.delete(`/chat/messages/${messageId}/reactions/${emoji}`),
  
  search: (query, channelId) =>
    apiClient.get(`/messages/search?q=${query}&channelId=${channelId}`),
};

// ============== DIRECT MESSAGES ==============
export const directMessageService = {
  list: () =>
    apiClient.get('/direct-messages'),
  
  conversations: () =>
    apiClient.get('/direct-messages/conversations'),
  
  getConversation: (userId) =>
    apiClient.get(`/direct-messages/conversations/${userId}`),
  
  messages: (conversationId, limit = 50, offset = 0) =>
    apiClient.get(`/direct-messages/${conversationId}?limit=${limit}&offset=${offset}`),
  
  send: (userId, content) =>
    apiClient.post('/direct-messages', { userId, content }),
};

// ============== USERS ==============
export const userService = {
  get: (userId) =>
    apiClient.get(`/users/${userId}`),
  
  search: (query) =>
    apiClient.get(`/users/search?q=${query}`),
  
  getFriends: () =>
    apiClient.get('/users/friends'),
  
  getFriendRequests: () =>
    apiClient.get('/users/friend-requests'),
  
  sendFriendRequest: (userId) =>
    apiClient.post('/users/friend-requests', { targetUserId: userId }),
  
  acceptFriendRequest: (requestId) =>
    apiClient.post(`/users/friend-requests/${requestId}/accept`),
  
  rejectFriendRequest: (requestId) =>
    apiClient.delete(`/users/friend-requests/${requestId}`),
  
  removeFriend: (userId) =>
    apiClient.delete(`/users/friends/${userId}`),
  
  getBlockedUsers: () =>
    apiClient.get('/users/blocked'),
  
  blockUser: (userId) =>
    apiClient.post('/users/block', { targetUserId: userId }),
  
  unblockUser: (userId) =>
    apiClient.delete(`/users/block/${userId}`),
  
  updateProfile: (data) =>
    apiClient.put('/users/profile', data),
  
  updateStatus: (status) =>
    apiClient.put('/users/status', { status }),
};

// ============== CALLS ==============
export const callService = {
  initiate: (userId) =>
    apiClient.post('/calls', { userId }),
  
  accept: (callId) =>
    apiClient.post(`/calls/${callId}/accept`),
  
  reject: (callId) =>
    apiClient.post(`/calls/${callId}/reject`),
  
  end: (callId) =>
    apiClient.post(`/calls/${callId}/end`),
  
  getActive: () =>
    apiClient.get('/calls/active'),
};

// ============== NOTIFICATIONS ==============
export const notificationService = {
  list: () =>
    apiClient.get('/notifications'),
  
  markAsRead: (notificationId) =>
    apiClient.put(`/notifications/${notificationId}/read`),
  
  markAllAsRead: () =>
    apiClient.post('/notifications/mark-all-read'),
  
  clear: (notificationId) =>
    apiClient.delete(`/notifications/${notificationId}`),
  
  clearAll: () =>
    apiClient.post('/notifications/clear-all'),
};

// ============== ADMIN ==============
export const adminService = {
  listUsers: () =>
    apiClient.get('/admin/users'),
  
  listServers: () =>
    apiClient.get('/admin/servers'),
  
  getStats: () =>
    apiClient.get('/admin/stats'),
  
  banUser: (userId, reason) =>
    apiClient.post('/admin/users/ban', { userId, reason }),
  
  unbanUser: (userId) =>
    apiClient.post('/admin/users/unban', { userId }),
  
  deleteServer: (serverId) =>
    apiClient.delete(`/admin/servers/${serverId}`),
};

export default apiClient;
